import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ErrorComponent } from '../ErrorComponent'
import { WorkspacesCard } from '../FluentCard/WorkspaceCard'
import { Loading } from '../Loading'
import {
  MessagesWorkspacesWorkspaceCard,
  MessagesSubscriptionsOrganisationTeam,
  MessagesApplicationsApplicationTeam,
} from '@/dotnet-client'

type MaintainAppWorkspaceProps = {
  selectedWorkspace: MessagesWorkspacesWorkspaceCard
  onSubmit?: () => void
  type: 'project' | 'support'
}

export const MaintainAppWorkspace = ({ selectedWorkspace, onSubmit, type }: MaintainAppWorkspaceProps) => {
  const queryClient = useQueryClient()

  const {
    getSubscriptionsApiGetOrganisationTeams,
    getApplicationsApiGetApplicationTeams,
    getWorkspacesApiGetProjectTeamsWorkspace,
    getWorkspacesApiGetSupportTeamsWorkspace,
    putWorkspacesApiAddProjectTeamToWorkspace,
    putWorkspacesApiAddSupportTeamToWorkspace,
    deleteWorkspacesApiDeleteProjectTeamFromWorkspace,
    deleteWorkspacesApiDeleteSupportTeamFromWorkspace,
  } = createRestClient()

  // ✅ Ensure the correct return type based on `type`
  const { data: allTeams = [], refetch: refetchAllTeams } =
    type === 'project'
      ? getSubscriptionsApiGetOrganisationTeams.useQuery({
          query: { orgId: selectedWorkspace.organisation_id },
        })
      : getApplicationsApiGetApplicationTeams.useQuery(
          {
            query: { orgId: selectedWorkspace.organisation_id, appId: selectedWorkspace.app_id },
          },
          { enabled: !!selectedWorkspace.workspaceId }
        )

  const addWorkspaceTeam =
    type === 'project'
      ? putWorkspacesApiAddProjectTeamToWorkspace.useMutation()
      : putWorkspacesApiAddSupportTeamToWorkspace.useMutation()

  const deleteWorkspaceTeam =
    type === 'project'
      ? deleteWorkspacesApiDeleteProjectTeamFromWorkspace.useMutation()
      : deleteWorkspacesApiDeleteSupportTeamFromWorkspace.useMutation()

  // ✅ Correct State Type
  const [selectedOptions, setSelectedOptions] = useState<
    MessagesSubscriptionsOrganisationTeam[] | MessagesApplicationsApplicationTeam[]
  >([])

  // ✅ Fetch currently added teams in the workspace
  const {
    data: currentTeams = [],
    isPending: isCurrentTeamsPending,
    isError: isCurrentTeamsError,
    refetch: refetchCurrentTeams,
  } = type === 'project'
    ? getWorkspacesApiGetProjectTeamsWorkspace.useQuery({
        query: { workspaceId: selectedWorkspace.workspaceId },
      })
    : getWorkspacesApiGetSupportTeamsWorkspace.useQuery({
        query: { workspaceId: selectedWorkspace.workspaceId },
      })

  // ✅ Remove teams from dropdown that are already in the workspace
  const availableTeams = allTeams
    ?.filter((t) => t.applicationId == selectedWorkspace.app_id)
    .filter((team) => !currentTeams.some((currentTeam) => currentTeam.id === team.id))

  const invalidateProjectTeams = () => {
    getWorkspacesApiGetProjectTeamsWorkspace.invalidateQueries(
      {
        parameters: {
          query: {
            workspaceId: selectedWorkspace.workspaceId,
          },
        },
      },
      queryClient
    )
  }

  const invalidateSupportTeams = () => {
    getWorkspacesApiGetSupportTeamsWorkspace.invalidateQueries(
      {
        parameters: {
          query: {
            workspaceId: selectedWorkspace.workspaceId,
          },
        },
      },
      queryClient
    )
  }

  // ✅ Handle Dropdown Selection
  const onOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    if (!availableTeams) return

    const _selectedOptions =
      type === 'project'
        ? (availableTeams as MessagesSubscriptionsOrganisationTeam[]).filter((team) =>
            data.selectedOptions.includes(team.id.toString())
          )
        : (availableTeams as MessagesApplicationsApplicationTeam[]).filter((team) =>
            data.selectedOptions.includes(team.id.toString())
          )

    setSelectedOptions(_selectedOptions)
  }

  // ✅ Handle Adding Teams
  const onAddSubmit = async () => {
    if (selectedOptions.length === 0) return

    try {
      await Promise.all(
        selectedOptions.map((team) =>
          addWorkspaceTeam.mutateAsync({
            body: { subscriptionId: selectedWorkspace.workspaceId, teamId: team.id },
          })
        )
      )
      toast.success(`Added ${selectedOptions.length} team(s)`)
      setSelectedOptions([])
      invalidateProjectTeams()
      invalidateSupportTeams()
      refetchAllTeams() // ✅ Ensure dropdown is updated
      refetchCurrentTeams() // ✅ Ensure added teams are reflected
      onSubmit?.()
    } catch (error: unknown) {
      toast.error(`Error adding team: ${String(error)}`)
    }
  }

  // ✅ Handle Deleting Teams
  const onDelete = async (teamId: number) => {
    try {
      await deleteWorkspaceTeam.mutateAsync({
        body: { subscriptionId: selectedWorkspace.workspaceId, teamId },
      })
      toast.success(`Deleted team`)
      invalidateProjectTeams()
      invalidateSupportTeams()
    } catch (error: unknown) {
      toast.error(`Error deleting team: ${String(error)}`)
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-5">
      {/* Multi-select dropdown */}
      <Dropdown
        aria-labelledby={'add-team-to-workspace'}
        multiselect={true}
        placeholder={`Add ${type === 'project' ? 'Project' : 'Support'} Teams`}
        value={selectedOptions.map((option) => `${option.name}`).join(', ')}
        selectedOptions={selectedOptions.map((option) => option.id.toString())}
        onOptionSelect={onOptionSelect}
      >
        <div className="max-h-[600px] overflow-y-scroll">
          {(availableTeams ?? []).map((option) => (
            <Option key={option.id} value={option.id.toString()}>
              {option.name}
            </Option>
          ))}
        </div>
      </Dropdown>

      <div className="flex flex-row justify-end">
        <Button disabled={selectedOptions.length === 0} text="Add teams" onClick={onAddSubmit} />
      </div>

      {isCurrentTeamsPending ? (
        <Loading />
      ) : isCurrentTeamsError ? (
        <ErrorComponent />
      ) : (
        currentTeams?.map((team) => (
          <WorkspacesCard
            key={team.id}
            workspace={{ title: team.name, subtitle: team.description || '', image: '' }}
            onDelete={() => onDelete(team.id)}
          />
        ))
      )}
    </div>
  )
}
